import React, { useState, useEffect } from 'react';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import cx from 'classnames';
import styled from 'styled-components';
import pageTypes from 'utils/pageTypes';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import useNewColorSchemaOptions from 'utils/hooks/useNewColorSchemaOptions';

const Text = styled.p`
  letter-spacing: 0.14px;
`;

const isStudio = (bedsLabel, currentPageType) => {
  if (bedsLabel) {
    const bedsNumber = bedsLabel.split(' ')[0];
    if (
      bedsNumber &&
      bedsNumber === '0' &&
      currentPageType === pageTypes.CondoPlan
    ) {
      return true;
    }
    return false;
  }
  return false;
};

const isZeroValue = (label) => {
  if (label) {
    const labelNumber = label.split(' ')[0];
    if (labelNumber && labelNumber === '0') return true;
    return false;
  }
  return true;
};

const getDefaultContentTextValue = (attributes) => {
  for (let i = 0; i < attributes.length; i++) {
    if (!isZeroValue(attributes[i].label)) return false;
  }
  return true;
};

const HomeSpecsBlock = ({ fields, navBar, lotJDEID, rvGarage = false }) => {
  const title = getFieldValue(fields, 'title') || [];
  const beds = title.find((e) => e.icon === 'bed') || {};
  const baths = title.find((e) => e.icon === 'bath') || {};
  const halfBaths = title.find((e) => e.icon === 'half-baths') || {};
  const sqft = title.find((e) => e.icon === 'ruler') || {};
  const garage = title.find((e) => e.icon === 'car') || {};
  const floor = title.find((e) => e.icon === 'floor') || {};
  const exposure = title.find((e) => e.icon === 'exposure') || {};
  const currentPageType = getFieldValue(fields, 'currentPageType');
  const productLine = getFieldValue(fields, 'productLine');
  const hasDen = getFieldValue(fields, 'den') || false;
  const denText = hasDen ? ' + Den' : '';
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);
  const { showNewSchemaColor } = useNewColorSchemaOptions();

  const dividerClassName = cx('ml-2 mr-2', {
    'md:text-mattamy-blue': !navBar && !showNewSchemaColor,
    'md:text-black': !navBar && showNewSchemaColor,
    'md:text-white': navBar,
  });
  const [hasSitemap, setHasSitemap] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      if (typeof window !== 'undefined' && window.document) {
        const exitsSitemap = window.document.getElementById('sitemap');
        if (exitsSitemap) setHasSitemap(true);
      }
    }, 0);
  }, []);

  const getCurrentLocation = () => {
    if (typeof window !== 'undefined' && window.document) {
      return window.location.pathname;
    }
    return '';
  };

  const removeDecimals = (sqft) => {
    if (sqft.includes('.00')) {
      return sqft.replace('.00', '');
    }
    return sqft;
  };

  const getLabel = (label) => {
    const labelValues = label.split(' ');
    if (labelValues && labelValues.length > 0) {
      if (labelValues[0][1] === '-') {
        return labelValues[0][2] > 1 ? label + 's' : label;
      }
      if (labelValues[0] !== '') {
        return labelValues[0] > 1 ? label + 's' : label;
      }
    }
    return '';
  };
  const getAttributeSpan = (tlabel, nextLabel, nextLine, denText = '') => {
    let label = tlabel;
    if (rvGarage && tlabel.includes('Car')) {
      label = label[0] + ' Car + RV Garage';
    }

    return (
      <>
        {label && !isZeroValue(label) ? (
          <span>{label + '' + denText}</span>
        ) : (
          ''
        )}
        {nextLabel && nextLabel !== '' && !isZeroValue(nextLabel) ? (
          !nextLine ? (
            <span className={dividerClassName}>|</span>
          ) : (
            <span className={dividerClassName}></span>
          )
        ) : (
          ''
        )}
      </>
    );
  };

  return (
    <>
      <Text
        className={cx('font-graphie', {
          'text-white text-base': navBar,
          'text-mattamy-blue ': !navBar && !showNewSchemaColor,
          'text-black ': !navBar && showNewSchemaColor,
          'text-sm md:text-xl flex flex-wrap justify-start leading-7': !navBar,
        })}
      >
        {!getDefaultContentTextValue(title) && (
          <>
            <span>
              {isStudio(beds.label, currentPageType)
                ? getAttributeSpan('Studio', baths.label, false)
                : getAttributeSpan(
                    getLabel(beds.label),
                    baths.label,
                    false,
                    denText,
                  )}
            </span>

            <span>
              {getAttributeSpan(getLabel(baths.label), halfBaths.label, false)}
            </span>

            <span>
              {getAttributeSpan(
                getLabel(halfBaths.label),
                removeDecimals(sqft.label),
                isMobile,
              )}
            </span>
            {!isMobile &&
              (currentPageType !== pageTypes.CondoPlan ? (
                <>
                  <span>
                    {getAttributeSpan(
                      removeDecimals(sqft.label),
                      removeDecimals(garage.label),
                      false,
                    )}
                  </span>
                  <span>
                    {getAttributeSpan(removeDecimals(garage.label), '', false)}
                  </span>
                </>
              ) : (
                <>
                  <span>
                    {getAttributeSpan(
                      removeDecimals(sqft.label),
                      garage.label,
                      false,
                    )}
                  </span>
                  <span>
                    {getAttributeSpan(
                      removeDecimals(garage.label),
                      floor.label,
                      false,
                    )}
                  </span>
                  <span>
                    {getAttributeSpan(floor.label, exposure.label, false)}
                  </span>
                  <span>{getAttributeSpan(exposure.label, '', false)}</span>
                </>
              ))}
          </>
        )}
        {lotJDEID && !isMobile && (
          <>
            <span className={dividerClassName}>|</span>
            {hasSitemap ? (
              <a href={getCurrentLocation() + '#sitemap'}>
                <span className='underline'>
                  {getAttributeSpan(lotJDEID, '', false)}
                </span>
              </a>
            ) : (
              <span>{getAttributeSpan(lotJDEID, '', false)}</span>
            )}
          </>
        )}
        {productLine && !isMobile && (
          <>
            <span className={dividerClassName}>|</span>
            <span>{getAttributeSpan(productLine, '', false)}</span>
          </>
        )}
      </Text>

      {!getDefaultContentTextValue(title) && isMobile && (
        <Text
          className={cx('font-graphie', {
            'text-white text-base': navBar,
            'text-mattamy-blue ': !navBar && !showNewSchemaColor,
            'text-black ': !navBar && showNewSchemaColor,
            'text-sm md:text-xl flex flex-wrap justify-start leading-7':
              !navBar,
          })}
        >
          <span>
            {getAttributeSpan(
              removeDecimals(sqft.label),
              removeDecimals(garage.label),
              false,
            )}
          </span>
          {currentPageType !== pageTypes.CondoPlan ? (
            <span>
              {getAttributeSpan(removeDecimals(garage.label), '', false)}
            </span>
          ) : (
            <>
              <span>
                {getAttributeSpan(
                  removeDecimals(garage.label),
                  floor.label,
                  false,
                )}
              </span>
              <span>
                {getAttributeSpan(floor.label, exposure.label, false)}
              </span>
              <span>{getAttributeSpan(exposure.label, '', false)}</span>
            </>
          )}
          {lotJDEID && (
            <>
              <span className={dividerClassName}>|</span>
              {hasSitemap ? (
                <a href={getCurrentLocation() + '#sitemap'}>
                  <span className='underline'>
                    {getAttributeSpan(lotJDEID, '', false)}
                  </span>
                </a>
              ) : (
                <span>{getAttributeSpan(lotJDEID, '', false)}</span>
              )}
            </>
          )}
          {productLine && (
            <>
              <span className={dividerClassName}>|</span>
              <span>{getAttributeSpan(productLine, '', false)}</span>
            </>
          )}
        </Text>
      )}
    </>
  );
};

export default HomeSpecsBlock;
